import {createContext, Dispatch, FC, SetStateAction, useContext, useMemo, useState} from "react";
import dayjs from "dayjs";
import {DateRangeFilterType, TeamProgressSection, TeamProgressWidget} from "@/enums";

export type ExportType = keyof typeof TeamProgressWidget

type ComponentState = "loading" | "loaded";
type SectionState = { [key: string]: ComponentState };

export type TeamProgressFilters = {
	archivedUsers: boolean,
	dateRangeType: DateRangeFilterType,
	dateFrom: Date,
	dateTo: Date
};

export interface TeamProgressContextValue {
	filters: TeamProgressFilters;
	sectionStates: SectionState;
	setFilters: Dispatch<SetStateAction<TeamProgressFilters>>;
	updateSectionState: (isLoading: boolean, sectionName: string) => void;
	allSectionsLoaded: () => boolean;
}

const TeamProgressContext = createContext<TeamProgressContextValue | undefined>(undefined);

export const useTeamProgressContext = () => {
	const context = useContext(TeamProgressContext);
	if (!context) {
		throw new Error("useTeamProgressContext must be used within a TeamProgressProvider");
	}
	return context;
}

interface TeamProgressProviderProps {
	children: React.ReactNode;
}

export const TeamProgressProvider: FC<TeamProgressProviderProps> = ({children}) => {
	const [filters, setFilters] = useState<TeamProgressFilters>({
		archivedUsers: false,
		dateRangeType: DateRangeFilterType.lastWeek,
		dateFrom: dayjs().startOf("week").toDate(),
		dateTo: dayjs().startOf("day").toDate()
	});

	const [sectionStates, setSectionStates] = useState<SectionState>({
		[TeamProgressSection.widgets] : "loading",
		[TeamProgressSection.chart] : "loading",
		[TeamProgressSection.dataGrid] : "loading"
	});

	const updateSectionState = (isLoading: boolean, sectionName: string) => {
		setSectionStates(previous => ({
			...previous,
			[sectionName]: isLoading ? "loading" : "loaded"
		}))
	}

	const allSectionsLoaded = () => {
		let sectionsLoaded = true;
		for(const [_, value] of Object.entries(sectionStates)){
			if (value === "loading") {
				sectionsLoaded = false;
				break;
			}
		}

		return sectionsLoaded;
	}

	const contextValue = useMemo(() => ({
		filters,
		sectionStates,
		setFilters,
		updateSectionState,
		allSectionsLoaded
	}), [filters, sectionStates])

	return (
		<TeamProgressContext.Provider value={contextValue}>
			{children}
		</TeamProgressContext.Provider>
	);
}